import React, { useEffect, useState, useCallback } from 'react';
import { Container, Table, Button, Modal, Form, InputGroup } from 'react-bootstrap';
import { forumApi } from '../../api';
import { getAccessDeniedMessage } from '../../utils/moderationErrors';
import HrnbCard from '../../components/controls/HrnbCard';
import HrnbAlert from '../../components/controls/HrnbAlert';
import HrnbLoader from '../../components/controls/HrnbLoader';
import AdminPagination from '../components/pagination/AdminPagination';
import './AdminForumCategories.css';

const AdminForumCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Пагинация
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    // Форма категории
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        iconUrl: '',
        sortOrder: 0
    });

    // Загрузка категорий
    const loadCategories = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await forumApi.listCategories();

            let categoriesList = [];
            let total = 0;

            if (Array.isArray(data)) {
                categoriesList = data;
                total = data.length;
            } else if (data && Array.isArray(data.content)) {
                categoriesList = data.content;
                total = data.totalElements || data.total || data.content.length;
            }
            
            setCategories(categoriesList);
            setTotalItems(total);
        } catch (err) {
            setError(`Не удалось загрузить категории: ${err.message}`);
            console.error('[AdminForumCategories] Failed to load categories:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    // Открытие модального окна для создания
    const handleCreate = () => {
        setEditingCategory(null);
        setFormData({
            name: '',
            description: '',
            iconUrl: '',
            sortOrder: 0
        });
        setShowModal(true);
    };

    // Открытие модального окна для редактирования
    const handleEdit = (category) => {
        setEditingCategory(category);
        setFormData({
            name: category.name || '',
            description: category.description || '',
            iconUrl: category.iconUrl || '',
            sortOrder: category.sortOrder || 0
        });
        setShowModal(true);
    };

    // Сохранение категории
    const handleSave = async () => {
        if (!formData.name.trim()) {
            setError('Название категории обязательно');
            return;
        }

        try {
            setActionLoading(true);
            setError(null);

            if (editingCategory) {
                await forumApi.updateCategory(editingCategory.id, formData);
            } else {
                await forumApi.createCategory(formData);
            }

            await loadCategories();
            setShowModal(false);
        } catch (err) {
            console.error('[AdminForumCategories] Failed to save category:', err);

            const accessDeniedMsg = getAccessDeniedMessage(err);
            if (accessDeniedMsg) {
                setError(accessDeniedMsg);
            } else if (err?.response?.status === 409 ||
                err?.message?.includes('optimistic') ||
                err?.message?.includes('version')) {
                setError('Не удалось сохранить категорию. Данные были изменены другим пользователем. Обновите страницу и попробуйте снова.');
            } else if (err?.response?.status === 400) {
                setError('Ошибка валидации. Проверьте введенные данные.');
            } else {
                setError(`Не удалось сохранить категорию: ${err.message}`);
            }
        } finally {
            setActionLoading(false);
        }
    };

    // Удаление категории
    const handleDelete = async (category) => {
        if (!window.confirm(`Вы уверены, что хотите удалить категорию "${category.name}"?\n\nЭто может привести к удалению связанных постов.`)) {
            return;
        }

        try {
            setActionLoading(true);
            setError(null);
            await forumApi.deleteCategory(category.id);
            await loadCategories();
        } catch (err) {
            console.error('[AdminForumCategories] Failed to delete category:', err);

            const accessDeniedMsg = getAccessDeniedMessage(err);
            if (accessDeniedMsg) {
                setError(accessDeniedMsg);
            } else if (err?.response?.status === 409) {
                setError('Не удалось удалить категорию. Она используется в других сущностях.');
            } else {
                setError(`Не удалось удалить категорию: ${err.message}`);
            }
        } finally {
            setActionLoading(false);
        }
    };

    // Фильтрация категорий по поиску
    const filteredCategories = categories.filter(cat =>
        cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cat.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Сортировка по sortOrder
    const sortedCategories = [...filteredCategories].sort((a, b) => 
        (a.sortOrder || 0) - (b.sortOrder || 0)
    );

    if (loading) {
        return (
            <Container fluid className="admin-loading">
                <HrnbLoader />
            </Container>
        );
    }

    return (
        <Container fluid>
            <div className="admin-header-row">
                <h1 className="admin-page-title">Управление категориями форума</h1>
                <Button variant="primary" onClick={handleCreate}>
                    + Добавить категорию
                </Button>
            </div>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <div className="admin-pagination-header">
                <Form.Select
                    className="admin-page-size-selector"
                    value={pageSize}
                    onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                    }}
                >
                    <option value={5}>5 на странице</option>
                    <option value={10}>10 на странице</option>
                    <option value={25}>25 на странице</option>
                    <option value={50}>50 на странице</option>
                </Form.Select>
            </div>

            <HrnbCard>
                <div className="admin-search-bar">
                    <InputGroup>
                        <InputGroup.Text>🔍</InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Поиск категорий..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </InputGroup>
                </div>

                <Table responsive hover className="admin-categories-table">
                    <thead>
                        <tr>
                            <th style={{ width: '60px' }}>#</th>
                            <th>Иконка</th>
                            <th>Название</th>
                            <th>Описание</th>
                            <th style={{ width: '100px' }}>Порядок</th>
                            <th style={{ width: '200px' }}>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedCategories.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="text-center text-muted">
                                    {searchQuery ? 'Категории не найдены' : 'Категорий пока нет. Добавьте первую!'}
                                </td>
                            </tr>
                        ) : (
                            sortedCategories.map((category, index) => (
                                <tr key={category.id}>
                                    <td>{index + 1}</td>
                                    <td className="category-icon">
                                        {category.iconUrl ? (
                                            <img src={category.iconUrl} alt="" className="category-icon-img" />
                                        ) : (
                                            <span className="category-icon-default">📁</span>
                                        )}
                                    </td>
                                    <td>
                                        <strong>{category.name}</strong>
                                    </td>
                                    <td className="text-muted">
                                        {category.description?.substring(0, 50)}
                                        {category.description?.length > 50 ? '...' : ''}
                                    </td>
                                    <td>{category.sortOrder || 0}</td>
                                    <td>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => handleEdit(category)}
                                            disabled={actionLoading}
                                        >
                                            Редактировать
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDelete(category)}
                                            disabled={actionLoading}
                                        >
                                            Удалить
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                <AdminPagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                />
            </HrnbCard>

            {/* Модальное окно создания/редактирования */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingCategory ? 'Редактирование категории' : 'Новая категория'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Название *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Например: Программирование"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Описание</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Краткое описание категории"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>URL иконки</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.iconUrl}
                                onChange={(e) => setFormData({ ...formData, iconUrl: e.target.value })}
                                placeholder="https://example.com/icon.svg"
                            />
                            <Form.Text className="text-muted">
                                Ссылка на SVG или PNG иконку (необязательно)
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Порядок отображения</Form.Label>
                            <Form.Control
                                type="number"
                                value={formData.sortOrder}
                                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                                min={0}
                            />
                            <Form.Text className="text-muted">
                                Чем меньше число, тем выше категория в списке
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Отмена
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSave}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Сохранение...' : (editingCategory ? 'Сохранить' : 'Создать')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminForumCategories;
